import assessmentsApi from '@/app/redux/endpoints/assessments';
import { useUpdateCustomLevelMutation } from '@/app/redux/endpoints/customLevels';
import { useUpdateGroupMutation } from '@/app/redux/endpoints/groups';
import {
  useDeleteStudentMutation,
  useUpdateStudentMutation,
} from '@/app/redux/endpoints/students';
import { useAppDispatch } from '@/app/redux/hooks';
import { useCustomLevels } from '@/app/redux/hooks/customLevels';
import { useStudentGroups } from '@/app/redux/hooks/groups';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import {
  openConfirmationDialogue,
  setAttemptedProFeature,
  setConfirmationDialogueCallback,
} from '@/app/redux/slices/modalsSlice';
import { Group } from '@/common/types/group';
import { Student } from '@/common/types/student';
import { FeatureSwitch } from '@/common/ui/FeatureGate';
import InitialsCircle from '@/common/ui/InitialsCircle';
import PlayButton from '@/common/ui/PlayButton';
import TableInitialsCircle from '@/common/ui/TableInitialsCircle';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { ProIcon } from '@/common/ui/UpgradeButton';
import { getLastLoginLabel } from '@/common/utils/datetime';
import { resetLevelProgress } from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import ManageGroupsDialog from '@/dashboard/LearnerList/ManageGroupsDialog';
import {
  CheckIcon,
  DotsThreeVerticalIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Checkbox } from '@shadcn/components/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import { Skeleton } from '@shadcn/components/skeleton';
import { TableCell, TableRow } from '@shadcn/components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { Info } from 'lucide-react';
import { memo, useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import LevelSelect from './LevelSelect';

interface LearnerListRowProps {
  student: Student;
  selected: boolean;
  onSelectedChange: (studentId: string) => void;
  handleLearnerLogin: (studentId: string) => void;
  handleCreateAssessment: (studentId: string) => void;
  group?: Group;
  isScrolling?: boolean;
}

export default memo(function LearnerListRow({
  student,
  selected,
  onSelectedChange,
  handleLearnerLogin,
  handleCreateAssessment,
  group,
  isScrolling,
}: LearnerListRowProps) {
  const navigate = useNavigate();
  const { groupId } = useParams();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const [updateStudent] = useUpdateStudentMutation();

  const studentGroups = useStudentGroups(student.id);
  const hasRenderedRef = useRef(false);

  const handleStudentLevelChange = useCallback(
    async (level: number, resetProgress: boolean) => {
      if (!currentOrganisationId) {
        console.error('Missing org ID');
        toast.error('Failed to update learner level', {
          description: UNKNOWN_ERROR_MESSAGE,
        });
        return;
      }
      await updateStudent({
        orgId: currentOrganisationId,
        studentId: student.id,
        updates: {
          level,
          levelsProgress: resetProgress
            ? resetLevelProgress(level, student.levelsProgress)
            : student.levelsProgress,
        },
      });
      trackMixpanelEvent('Change Student Level', {
        studentId: student.id,
        level,
        resetProgress,
      });
    },
    [currentOrganisationId, updateStudent, student],
  );

  if (isScrolling && !hasRenderedRef.current) {
    return (
      <TableRow>
        <TableCell>
          <Skeleton className="w-full h-6" />
        </TableCell>
        <TableCell>
          <Skeleton className="w-full h-6" />
        </TableCell>
        <TableCell>
          <Skeleton className="w-full h-6" />
        </TableCell>
        <TableCell className="pr-6">
          <Skeleton className="w-full h-6" />
        </TableCell>
        <TableCell>
          <Skeleton className="w-full h-6" />
        </TableCell>
        <TableCell>
          <Skeleton className="w-full h-6" />
        </TableCell>
      </TableRow>
    );
  }

  hasRenderedRef.current = true;

  function handleNavigateToStudent() {
    trackMixpanelEvent('Opened individual learner view', {
      studentId: student.id,
      organisationId: student.organisationId,
    });
    const learnerPart = `learners/${student.id}`;
    const next = groupId
      ? `/dashboard/groups/${groupId}/${learnerPart}`
      : `/dashboard/${learnerPart}`;
    navigate(next);
  }

  return (
    <TableRow className="cursor-pointer" onClick={handleNavigateToStudent}>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selected}
          onCheckedChange={() => onSelectedChange(student.id)}
          onClick={(e) => e.stopPropagation()}
          className="mx-2"
        />
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-3">
          <TableInitialsCircle
            userName={`${student.studentFirstName} ${student.studentSurname}`}
            size={24}
          />
          <div className="flex flex-col min-w-0 py-1">
            <div className="font-medium truncate">
              {student.studentFirstName} {student.studentSurname}
            </div>
            <div className="text-sm text-muted-foreground truncate">
              {student.spellcastUsername}
            </div>
          </div>
        </div>
      </TableCell>

      <TableCell>
        {student.lastLogin > 0 ? (
          <div className="text-xs text-muted-foreground truncate">
            {getLastLoginLabel(student.lastLogin)}
          </div>
        ) : null}
      </TableCell>

      <TableCell className="pr-6">
        <LevelSelect
          student={student}
          onLevelChange={handleStudentLevelChange}
        />
      </TableCell>

      <TableCell>
        <div className="flex items-center pl-1">
          {studentGroups.slice(0, 2).map((group) => (
            <Tooltip key={group.id}>
              <TooltipTrigger
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/dashboard/groups/${group.id}`);
                }}
              >
                <div className="ml-[-6px]">
                  <InitialsCircle userName={group.name} size={20} />
                </div>
              </TooltipTrigger>
              <TooltipContent>{group.name}</TooltipContent>
            </Tooltip>
          ))}
          {studentGroups.length > 2 && (
            <Tooltip>
              <TooltipTrigger>
                <div className="ml-[-6px]">
                  <InitialsCircle
                    userName={`+${studentGroups.length - 2}`}
                    size={20}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {studentGroups
                  .slice(2)
                  .map((group) => group.name)
                  .join(', ')}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </TableCell>

      <RowDropdownCell
        student={student}
        group={group}
        handleLearnerLogin={handleLearnerLogin}
        handleCreateAssessment={handleCreateAssessment}
      />
    </TableRow>
  );
});

interface RowDropdownCellProps {
  student: Student;
  group?: Group;
  handleLearnerLogin: (studentId: string) => void;
  handleCreateAssessment: (studentId: string) => void;
}

const RowDropdownCell = memo(function RowDropdownCell({
  student,
  group,
  handleLearnerLogin,
  handleCreateAssessment,
}: RowDropdownCellProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentOrganisationId } = useCurrentOrganisationId();

  const [deleteStudent] = useDeleteStudentMutation();
  const [updateStudent] = useUpdateStudentMutation();
  const [updateCustomLevel] = useUpdateCustomLevelMutation();
  const [updateGroup] = useUpdateGroupMutation();
  const { customLevels } = useCustomLevels();

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isManageGroupsDialogOpen, setIsManageGroupsDialogOpen] =
    useState(false);

  function handleConfirmDeleteStudent() {
    setConfirmationDialogueCallback(handleDeleteStudent);
    dispatch(
      openConfirmationDialogue({
        title: 'Delete Learner',
        description: `Are you sure you want to delete ${student.studentFirstName} ${student.studentSurname}? This action cannot be undone.`,
        variant: 'destructive',
      }),
    );
  }

  async function handleDeleteStudent() {
    if (!currentOrganisationId) {
      console.error('Missing org ID');
      toast.error('Failed to delete learner', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    try {
      const { deletedAssessmentIds } = await deleteStudent({
        orgId: currentOrganisationId,
        studentId: student.id,
      }).unwrap();
      if (deletedAssessmentIds.length > 0) {
        dispatch(
          assessmentsApi.util.updateQueryData(
            'getAssessmentsForOrganisation',
            currentOrganisationId,
            (draft) => {
              const ids = new Set(deletedAssessmentIds);
              return draft.filter((a) => !ids.has(a.id));
            },
          ),
        );
      }
      trackMixpanelEvent('Learner Deleted', {
        studentId: student.id,
      });
      toast.success('Learner deleted');
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Failed to delete learner', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  function handleConfirmRemoveFromGroup() {
    if (!group) {
      console.error('No group provided');
      toast.error('Failed to remove learner from group', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    setConfirmationDialogueCallback(handleRemoveFromGroup);
    dispatch(
      openConfirmationDialogue({
        title: 'Remove learner from group',
        description: [
          `Are you sure you want to remove ${student.studentFirstName} ${student.studentSurname} from ${group.name}?`,
          'This will not delete the learner, but they will no longer be in this group.',
        ],
        variant: 'destructive',
      }),
    );
  }

  async function handleRemoveFromGroup() {
    if (!group?.id || !currentOrganisationId) {
      console.error('Group not found');
      toast.error('Failed to remove learner from group', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    try {
      await updateGroup({
        orgId: currentOrganisationId,
        groupId: group.id,
        updates: {
          studentIds: group.studentIds.filter((id) => id !== student.id),
        },
      }).unwrap();
      toast.success('Learner removed from group');
    } catch (error) {
      console.error('Error removing learner from group:', error);
      toast.error('Failed to remove learner from group', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  async function handleAssignCustomLevel(levelId: string | null) {
    if (!currentOrganisationId) return;
    setDropdownOpen(false);
    try {
      const previousLevel = customLevels.find(
        (l) => l.id === student.customLevelId,
      );
      const newLevel = customLevels.find((l) => l.id === levelId);

      await updateStudent({
        orgId: currentOrganisationId,
        studentId: student.id,
        updates: { customLevelId: levelId },
      }).unwrap();

      if (previousLevel) {
        const updatedStudents = { ...previousLevel.students };
        delete updatedStudents[student.id];
        await updateCustomLevel({
          orgId: currentOrganisationId,
          id: previousLevel.id,
          updates: { students: updatedStudents },
        }).unwrap();
      }

      if (newLevel) {
        await updateCustomLevel({
          orgId: currentOrganisationId,
          id: newLevel.id,
          updates: {
            students: {
              ...newLevel.students,
              [student.id]: { completedPlaythroughs: 0, gamesProgress: {} },
            },
          },
        }).unwrap();
      }
    } catch (error) {
      console.error('Error assigning custom level:', error);
      toast.error('Failed to assign custom level', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  function handleGenerateLessonPlan() {
    trackMixpanelEvent('Generate lesson plan', {
      studentId: student.id,
      organisationId: student.organisationId,
    });
    navigate(`/dashboard/lessonplans/new?level=${student.level}`);
  }

  function handleGenerateCustomLevel() {
    trackMixpanelEvent('Generate custom level', {
      studentId: student.id,
      organisationId: student.organisationId,
    });
    navigate(
      `/dashboard/customlevels/new?level=${student.level}&studentId=${student.id}`,
    );
  }

  return (
    <TableCell className="w-24">
      <div className="flex justify-evenly gap-2">
        <FeatureSwitch
          feature="game"
          enabled={
            <PlayButton
              onClick={(e) => {
                e.stopPropagation();
                handleLearnerLogin(student.id);
              }}
            />
          }
          disabled={
            <PlayButton
              onClick={(e) => {
                e.stopPropagation();
                dispatch(setAttemptedProFeature('Spellcaster Game'));
              }}
            >
              Play
              <ProIcon />
            </PlayButton>
          }
        />

        <DropdownMenu open={isDropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="p-1/2 mx-1 mt-1"
              onClick={(e) => e.stopPropagation()}
            >
              <DotsThreeVerticalIcon size={24} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
            <DropdownMenuLabel>
              {student.studentFirstName} {student.studentSurname}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!group && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  setIsManageGroupsDialogOpen(true);
                }}
              >
                Manage groups
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleGenerateLessonPlan}>
              Generate lesson plan
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleGenerateCustomLevel}>
              Generate custom level
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="gap-2">
                Assign custom level
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {customLevels.length === 0 && (
                  <DropdownMenuItem disabled>
                    No custom levels yet
                  </DropdownMenuItem>
                )}
                {customLevels.map((level) => (
                  <DropdownMenuItem
                    key={level.id}
                    className="gap-2"
                    onClick={() =>
                      handleAssignCustomLevel(
                        student.customLevelId === level.id ? null : level.id,
                      )
                    }
                  >
                    <CheckIcon
                      size={14}
                      className={
                        student.customLevelId === level.id
                          ? 'opacity-100'
                          : 'opacity-0'
                      }
                    />
                    {level.description || 'Untitled Level'}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <FeatureSwitch
              feature="assessments"
              enabled={
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* wrap menu item in span so tooltip doesn't get disabled */}
                    <span>
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDropdownOpen(false);
                          handleCreateAssessment(student.id);
                        }}
                        disabled={!!student.assessmentId}
                      >
                        <span>Create assessment</span>
                        {student.assessmentId && (
                          <Info color="hsl(var(--primary))" size={16} />
                        )}
                      </DropdownMenuItem>
                    </span>
                  </TooltipTrigger>
                  {student.assessmentId && (
                    <TooltipContent>
                      {student.studentFirstName} {student.studentSurname}{' '}
                      already has an active assessment
                    </TooltipContent>
                  )}
                </Tooltip>
              }
              disabled={
                <DropdownMenuItem
                  className="gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(false);
                    dispatch(setAttemptedProFeature('Assessments'));
                  }}
                >
                  <span>Create Assessment</span>
                  <ProIcon />
                </DropdownMenuItem>
              }
            />
            <DropdownMenuSeparator />
            {group && (
              <DropdownMenuItem
                className="flex gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  handleConfirmRemoveFromGroup();
                }}
              >
                <TrashIcon weight="bold" size={16} color="red" />
                Remove from group
              </DropdownMenuItem>
            )}
            {!group && (
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => {
                  setDropdownOpen(false);
                  handleConfirmDeleteStudent();
                }}
              >
                <TrashIcon weight="bold" size={16} color="red" /> Delete learner
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {isManageGroupsDialogOpen && (
          <ManageGroupsDialog
            isOpen={isManageGroupsDialogOpen}
            onClose={() => setIsManageGroupsDialogOpen(false)}
            student={student}
          />
        )}
      </div>
    </TableCell>
  );
});
